import React from 'react';
import { Box, Card, Typography, Button } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { V3_LAYOUT } from '../constants/layoutConstants';
import RefreshIcon from '@mui/icons-material/Refresh';

class V3ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log error details for debugging
    console.error('V3ErrorBoundary caught an error:', error, errorInfo);
    this.setState({
      error: error,
      errorInfo: errorInfo
    });
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  render() {
    if (this.state.hasError) {
      return (
        <Box sx={{ 
          p: V3_LAYOUT.CONTENT_PADDING,
          pt: V3_LAYOUT.HEADER_CLEARANCE,
          pb: V3_LAYOUT.BOTTOM_NAV_CLEARANCE,
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <Card sx={{ 
            p: 4, 
            maxWidth: 600, 
            width: '100%',
            textAlign: 'center',
            bgcolor: 'background.paper',
            boxShadow: 3
          }}>
            <ErrorOutlineIcon 
              sx={{ 
                fontSize: 64, 
                color: 'error.main',
                mb: 2
              }} 
            />
            <Typography variant="h5" gutterBottom color="error">
              Something went wrong
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              {this.props.fallbackMessage || 'An unexpected error occurred while loading this content.'}
            </Typography>
            
            {/* Show error details in development */}
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <Card sx={{ 
                p: 2, 
                mb: 3, 
                bgcolor: 'grey.100',
                textAlign: 'left'
              }}>
                <Typography variant="caption" component="pre" sx={{ 
                  fontFamily: 'monospace',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word'
                }}>
                  {this.state.error.toString()}
                  {this.state.errorInfo && this.state.errorInfo.componentStack}
                </Typography>
              </Card>
            )}
            
            <Button
              variant="contained"
              startIcon={<RefreshIcon />}
              onClick={this.handleReset}
              sx={{ mt: 2 }}
            >
              Try Again
            </Button>
          </Card>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default V3ErrorBoundary;